"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, privateMap, value) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to set private field on non-instance");
    }
    privateMap.set(receiver, value);
    return value;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, privateMap) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to get private field on non-instance");
    }
    return privateMap.get(receiver);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
var _localPosition, _localDirection, _localInverseQuaterinion, _remoteParticipants, _environment, _waitingForSend, _conferenceId, _peerId;
Object.defineProperty(exports, "__esModule", { value: true });
exports.SpatialAudioManager = void 0;
const VoxeetApi_1 = __importDefault(require("../api/VoxeetApi"));
const SpatialAudio_1 = require("../models/SpatialAudio");
const Exceptions_1 = require("./Exceptions");
const events_1 = require("../events");
const EventBusInstance_1 = __importDefault(require("../events/EventBusInstance"));
const Exceptions_2 = require("./Exceptions");
class SpatialAudioManager {
    constructor(conferenceId, peerId) {
        _localPosition.set(this, void 0);
        _localDirection.set(this, void 0);
        _localInverseQuaterinion.set(this, void 0);
        _remoteParticipants.set(this, void 0);
        _environment.set(this, void 0);
        this.transformedParticipants = {};
        _waitingForSend.set(this, false);
        _conferenceId.set(this, void 0);
        _peerId.set(this, void 0);
        __classPrivateFieldSet(this, _localPosition, new SpatialAudio_1.SpatialPosition());
        __classPrivateFieldSet(this, _localDirection, new SpatialAudio_1.SpatialDirection());
        __classPrivateFieldSet(this, _localInverseQuaterinion, this.toInverseQuaternion(__classPrivateFieldGet(this, _localDirection)));
        __classPrivateFieldSet(this, _remoteParticipants, new Map());
        __classPrivateFieldSet(this, _environment, new SpatialAudio_1.SpatialEnvironment());
        __classPrivateFieldSet(this, _conferenceId, conferenceId);
        __classPrivateFieldSet(this, _peerId, peerId);
    }
    toDegrees(angle) {
        return angle * (180 / Math.PI);
    }
    toRadians(angle) {
        return angle * (Math.PI / 180);
    }
    dotProduct(v1, v2) {
        return v1.x * v2.x + v1.y * v2.y + v1.z * v2.z;
    }
    crossProduct(v1, v2) {
        return SpatialAudio_1.SpatialPosition.fromData(v1.y * v2.z - v1.z * v2.y, v1.z * v2.x - v1.x * v2.z, v1.x * v2.y - v1.y * v2.x);
    }
    magnitude(v) {
        return Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
    }
    normalize(v) {
        let m = this.magnitude(v);
        return SpatialAudio_1.SpatialPosition.fromData(v.x / m, v.y / m, v.z / m);
    }
    similarValue(value, expected, epsilon) {
        if (value < expected - epsilon) {
            return false;
        }
        else if (value > expected + epsilon) {
            return false;
        }
        else {
            return true;
        }
    }
    setEnvironment(environment) {
        environment.forward = this.normalize(environment.forward);
        environment.up = this.normalize(environment.up);
        environment.right = this.normalize(environment.right);
        if (!this.similarValue(this.dotProduct(environment.forward, environment.up), 0, 0.00001)) {
            throw new Exceptions_1.ParameterError('SpatialEnvironment::forward and SpatialEnvironment::up parameters must be orthogonal vectors');
        }
        if (!this.similarValue(this.dotProduct(environment.forward, environment.right), 0, 0.00001)) {
            throw new Exceptions_1.ParameterError('SpatialEnvironment::forward and SpatialEnvironment::right parameters must be orthogonal vectors');
        }
        if (!this.similarValue(this.dotProduct(environment.right, environment.up), 0, 0.00001)) {
            throw new Exceptions_1.ParameterError('SpatialEnvironment::right and SpatialEnvironment::up parameters must be orthogonal vectors');
        }
        if (environment.scale.x <= 0) {
            throw new Exceptions_1.ParameterError('Scale.x must be a positive non-zero number');
        }
        if (environment.scale.y <= 0) {
            throw new Exceptions_1.ParameterError('Scale.y must be a positive non-zero number');
        }
        if (environment.scale.z <= 0) {
            throw new Exceptions_1.ParameterError('Scale.z must be a positive non-zero number');
        }
        __classPrivateFieldSet(this, _environment, environment);
        this.updateRelativePositions();
    }
    setRemotePosition(participant, position) {
        if (position === null) {
            __classPrivateFieldGet(this, _remoteParticipants).delete(participant.id);
        }
        else {
            let remoteParticipant = __classPrivateFieldGet(this, _remoteParticipants).get(participant.id);
            if (!remoteParticipant) {
                remoteParticipant = new SpatialAudio_1.SpatialRemoteParticipant();
                remoteParticipant.participant = participant;
                __classPrivateFieldGet(this, _remoteParticipants).set(participant.id, remoteParticipant);
            }
            remoteParticipant.position = position;
        }
        this.updateRelativePositions();
    }
    setLocalPosition(position) {
        __classPrivateFieldSet(this, _localPosition, position);
        this.updateRelativePositions();
    }
    setLocalDirection(direction) {
        __classPrivateFieldSet(this, _localDirection, direction);
        __classPrivateFieldSet(this, _localInverseQuaterinion, this.toInverseQuaternion(direction));
        this.updateRelativePositions();
    }
    transformRemotePosition(position) {
        //Translate
        let translated = new SpatialAudio_1.SpatialPosition();
        translated.x = position.x - __classPrivateFieldGet(this, _localPosition).x;
        translated.y = position.y - __classPrivateFieldGet(this, _localPosition).y;
        translated.z = position.z - __classPrivateFieldGet(this, _localPosition).z;
        // We apply the scale in the applications world space (before the local direction rotation)
        // Otherwise we would need to apply the rotation to the scale vector as well
        // Scale
        let scaled = new SpatialAudio_1.SpatialPosition();
        scaled.x = translated.x / __classPrivateFieldGet(this, _environment).scale.x;
        scaled.y = translated.y / __classPrivateFieldGet(this, _environment).scale.y;
        scaled.z = translated.z / __classPrivateFieldGet(this, _environment).scale.z;
        //Rotate
        let rotated = new SpatialAudio_1.SpatialPosition();
        let angle = __classPrivateFieldGet(this, _localInverseQuaterinion);
        const ww = angle.w * angle.w;
        const xx = angle.x * angle.x;
        const yy = angle.y * angle.y;
        const zz = angle.z * angle.z;
        const wx = angle.w * angle.x;
        const wy = angle.w * angle.y;
        const wz = angle.w * angle.z;
        const xy = angle.x * angle.y;
        const xz = angle.x * angle.z;
        const yz = angle.y * angle.z;
        rotated.x = ww * scaled.x + 2 * wy * scaled.z - 2 * wz * scaled.y + xx * scaled.x + 2 * xy * scaled.y + 2 * xz * scaled.z - zz * scaled.x - yy * scaled.x;
        rotated.y = 2 * xy * scaled.x + yy * scaled.y + 2 * yz * scaled.z + 2 * wz * scaled.x - zz * scaled.y + ww * scaled.y - 2 * wx * scaled.z - xx * scaled.y;
        rotated.z = 2 * xz * scaled.x + 2 * yz * scaled.y + zz * scaled.z - 2 * wy * scaled.x - yy * scaled.z + 2 * wx * scaled.y - xx * scaled.z + ww * scaled.z;
        // Now to apply the coordinate system axis transform
        //
        // This simple implementation finds the dot product of the position
        // with the vector specified as forward/up/right in the customers
        // coordinate system. The dot product tells us how much of the
        // given position vetor contributes to the specified line vector
        // which represents the axis for forward/up/right
        //
        // We then just interpret those as +Z,+Y,+X which is the audio space
        // mapping for forward/up/right
        let converted = new SpatialAudio_1.SpatialPosition();
        converted.x = this.dotProduct(__classPrivateFieldGet(this, _environment).right, rotated);
        converted.y = this.dotProduct(__classPrivateFieldGet(this, _environment).up, rotated);
        converted.z = this.dotProduct(__classPrivateFieldGet(this, _environment).forward, rotated);
        return converted;
    }
    toInverseQuaternion(direction) {
        let quaternion = { w: 0, x: 0, y: 0, z: 0 };
        const cx = Math.cos(this.toRadians(direction.x) * 0.5);
        const sx = Math.sin(this.toRadians(direction.x) * 0.5);
        const cy = Math.cos(this.toRadians(direction.y) * 0.5);
        const sy = Math.sin(this.toRadians(direction.y) * 0.5);
        const cz = Math.cos(this.toRadians(direction.z) * 0.5);
        const sz = Math.sin(this.toRadians(direction.z) * 0.5);
        quaternion.w = cx * cy * cz + sx * sy * sz;
        quaternion.x = sx * cy * cz + cx * sy * sz;
        quaternion.y = cx * sy * cz - sx * cy * sz;
        quaternion.z = cx * cy * sz - sx * sy * cz;
        // Inverting the quaternion
        quaternion.x *= -1;
        quaternion.y *= -1;
        quaternion.z *= -1;
        return quaternion;
    }
    updateRelativePositions() {
        __classPrivateFieldGet(this, _remoteParticipants).forEach((remoteParticipant, participant_id) => {
            this.transformedParticipants[participant_id] = this.cartesianToPolar(this.transformRemotePosition(remoteParticipant.position));
        });
        if (!__classPrivateFieldGet(this, _waitingForSend)) {
            __classPrivateFieldSet(this, _waitingForSend, true);
            setTimeout(() => {
                this.sendPositions();
            }, 300);
        }
    }
    sendPositions() {
        return __awaiter(this, void 0, void 0, function* () {
            let request = {
                conferenceId: __classPrivateFieldGet(this, _conferenceId),
                peerId: __classPrivateFieldGet(this, _peerId),
                data: { spatialPositions: {} },
            };
            request.data.spatialPositions = Object.assign({}, this.transformedParticipants);
            this.transformedParticipants = {};
            __classPrivateFieldSet(this, _waitingForSend, false);
            //Don't send request if there is no position data
            if (Object.keys(request.data.spatialPositions).length > 0) {
                try {
                    yield VoxeetApi_1.default.conferences.audioPosition(request);
                }
                catch (e) {
                    EventBusInstance_1.default.send(events_1.Events.ConferenceGlobalError.fromData(new Exceptions_2.SpatialAudioError('Unable to set spatial audio position: reason: ' + e.reason)));
                }
            }
        });
    }
    cartesianToPolar(position) {
        let polar = new SpatialAudio_1.PolarPosition();
        polar.distance = Math.sqrt(position.x * position.x + position.y * position.y + position.z * position.z);
        if (polar.distance === 0) {
            polar.elevation = 0;
            polar.azimuth = 0;
            return polar;
        }
        let azimuth = Math.atan2(-1 * position.x, position.z);
        let elevation = Math.asin(position.y / polar.distance);
        polar.azimuth = this.toDegrees(azimuth);
        // TODO Temporary work around until backend update.
        if (polar.azimuth == -180) {
            polar.azimuth = 180;
        }
        polar.elevation = this.toDegrees(elevation);
        return polar;
    }
    get environment() {
        return __classPrivateFieldGet(this, _environment);
    }
}
exports.SpatialAudioManager = SpatialAudioManager;
_localPosition = new WeakMap(), _localDirection = new WeakMap(), _localInverseQuaterinion = new WeakMap(), _remoteParticipants = new WeakMap(), _environment = new WeakMap(), _waitingForSend = new WeakMap(), _conferenceId = new WeakMap(), _peerId = new WeakMap();
